import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { TableSpinner } from "../../components";
import { NoResultFound } from "../../components/Common";
import { QuestionDto, State, UserProfileDto } from "../../interfaces";
import {
  deleteQuestion,
  deleteQuestionConsent,
  getQuestionsByLesson,
  toggleAddEditQuestionModal,
  toggleQuestionState,
} from "../../store/actions";
import { QuestionModelEnum, RoleEnum } from "../../enums";
import smallImage13 from "../../assets/images/small/img-13.png";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditQuestionsModal } from "./AddEditQuestionModal";

type TProps = {
  subjectId: string;
  lessonId: string;
  model: QuestionModelEnum;
};
export const QuestionsPage: React.FC<TProps> = ({
  lessonId,
  model,
  subjectId,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  //#endregion

  //#region internal state
  const [showQuestionDeleteModal, setShowQuestionDeleteModal] =
    useState<boolean>(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(
    null
  );
  const [showAddEditQuestionModal, setShowAddEditQuestionModal] =
    useState<boolean>(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  //#endregion

  //#region redux state
  const { loading: loadingQuestions, response: questions } = useSelector(
    (state: any): State<QuestionDto[]> => state.question.questionsByLesson
  );
  const {
    loading: deleteQuestionLoading,
    success: deleteQuestionSuccess,
    error: deleteQuestionError,
  } = useSelector(
    (state: any): State<QuestionDto> => state.question.deleteQuestion
  );

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region effects
  useEffect(() => {
    dispatch(getQuestionsByLesson(lessonId, model));
  }, [lessonId]);

  useEffect(() => {
    if (!deleteQuestionLoading && deleteQuestionSuccess) {
      setDeletingQuestionId(null);
      setShowQuestionDeleteModal(false);
      dispatch(getQuestionsByLesson(lessonId, model));
    }
  }, [deleteQuestionLoading]);
  //#endregion

  //#region handlers

  const onAdd = () => {
    dispatch(toggleAddEditQuestionModal());
    setShowAddEditQuestionModal(true);
  };

  const onEdit = (id: string) => {
    setEditingQuestionId(id);
    setShowAddEditQuestionModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingQuestionId(id);
    setShowQuestionDeleteModal(true);
    dispatch(deleteQuestionConsent(id));
  };

  const onDeleteConfirm = () => {
    dispatch(deleteQuestion(deletingQuestionId as string, history));
    setDeletingQuestionId(null);
  };

  const onToggleState = (id: string) => {
    dispatch(toggleQuestionState(id as string, history));
  };

  //#endregion

  return (
    <React.Fragment>
      <Row className="mb-4 align-items-center">
        <Col className="col-sm">
          <div className="d-flex justify-content-sm-end gap-2">
            <Button color="success" onClick={onAdd}>
              <i className="ri-add-fill me-1 align-bottom"></i>
              {t("addItem", { item: "question" })}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        {loadingQuestions && <TableSpinner />}
        {!loadingQuestions && (!questions || questions.length == 0) && (
          <NoResultFound />
        )}
        {!loadingQuestions && questions && questions.length > 0 && (
          <React.Fragment>
            <Col lg={12}>
              <Row className="team-list grid-view-filter">
                {questions.map((item: QuestionDto, key) => (
                  <Col key={key}>
                    <Card className="team-box">
                      <img
                        src={
                          item.contents.find((i) => i.type === "image")
                            ?.image || smallImage13
                        }
                        alt=""
                        style={{ objectFit: "cover", height: 250 }}
                      />
                      <CardBody>
                        <UncontrolledDropdown
                          style={
                            i18n.language === "ar"
                              ? { position: "absolute", top: 10, left: 15 }
                              : { position: "absolute", top: 10, right: 15 }
                          }
                        >
                          <DropdownToggle
                            tag="a"
                            id="dropdownMenuLink2"
                            role="button"
                          >
                            <i className="ri-more-fill fs-17"></i>
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem
                              style={{
                                alignSelf: "flex-start",
                                display: "flex",
                              }}
                              className="dropdown-item edit-list"
                              onClick={() => onEdit(item.id)}
                            >
                              <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                              {t("edit")}
                            </DropdownItem>
                            {profile &&
                              profile.role.key == RoleEnum.SUPER_ADMIN && (
                                <DropdownItem
                                  style={{
                                    alignSelf: "flex-start",
                                    display: "flex",
                                  }}
                                  className="dropdown-item edit-list"
                                  onClick={() => onToggleState(item.id)}
                                >
                                  <i
                                    className={`${
                                      item.isActive
                                        ? "ri-forbid-line"
                                        : "ri-checkbox-circle-line text-danger"
                                    }  me-2 align-bottom text-muted`}
                                  ></i>
                                  {item.isActive
                                    ? t("inactivate")
                                    : t("activate")}
                                </DropdownItem>
                              )}
                            <DropdownItem
                              style={{
                                alignSelf: "flex-start",
                                display: "flex",
                              }}
                              className="dropdown-item remove-list"
                              onClick={() => onDelete(item.id)}
                            >
                              <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                              {t("delete")}
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>

                        <Row>
                          <Col sm={12} className="mb-2">
                            <Badge
                              color={`${
                                item.isActive === true ? "primary" : "danger"
                              }`}
                            >
                              {item.isActive === true
                                ? t("active")
                                : t("inActive")}
                            </Badge>
                            &nbsp;
                            {profile?.role.key === RoleEnum.SUPER_ADMIN && (
                              <Badge
                                color={`${
                                  item.skills.length <= 2 ? "danger" : "primary"
                                }`}
                              >
                                {t("skills")}({item.skills.length})
                              </Badge>
                            )}
                          </Col>
                          <Col sm={12}>
                            <div
                              style={{ maxHeight: "40px" }}
                              className="text-dark"
                              dangerouslySetInnerHTML={{
                                __html:
                                  item.contents.find((i) => i.type === "text")
                                    ?.text || "",
                              }}
                            ></div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </React.Fragment>
        )}
      </Row>
      <DeleteModal
        loading={deleteQuestionLoading}
        show={showQuestionDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowQuestionDeleteModal(false);
        }}
        error={deleteQuestionError}
      />
      <AddEditQuestionsModal
        lessonId={lessonId}
        subjectId={subjectId}
        id={editingQuestionId}
        show={showAddEditQuestionModal}
        onCloseClick={() => {
          dispatch(toggleAddEditQuestionModal());
          setShowAddEditQuestionModal(false);
          setEditingQuestionId(null);
          dispatch(getQuestionsByLesson(lessonId, model));
        }}
        model={model}
      />
    </React.Fragment>
  );
};
